import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import last from 'lodash/last';
import { WayPoint } from '@bringg/types';

import { formatDate } from './formatted-time-window';
import { Exception } from '../../../types/common.consts';
import { useStores } from '../../../recipes';

type ExceptionDateKeys = Pick<Exception, 'created_at' | 'scheduled_at' | 'alert_time'>;
type WayPointDateKeys = Pick<WayPoint, 'eta' | 'etl' | 'scheduled_at'>;

interface CommonTimeCellProps {
	dateString: string;
	timezone: string;
}

export const CommonTimeCell = observer(({ dateString, timezone }: CommonTimeCellProps) => {
	const {
		merchantConfigurationsStore: { dateFormatTimeFirst }
	} = useStores();

	const formattedDate = useMemo(
		() => formatDate(dateString, dateFormatTimeFirst, timezone),
		[dateString, timezone, dateFormatTimeFirst]
	);
	const formattedTimezone = useMemo(() => (timezone ? `(${timezone})` : ''), [timezone]);

	if (!dateString) {
		return <>-- : --</>;
	}
	return (
		<span title={`${formattedDate} ${formattedTimezone}`}>
			{formattedDate}
			{formattedTimezone && <span className="time-cell-timezone">{formattedTimezone}</span>}
		</span>
	);
});

interface TimeCellProps {
	alertId: string;
	dateKey: keyof ExceptionDateKeys;
}

// TODO: think of reusing common logic for TimeCell / TimeWindowCell
export const TimeCell = observer(({ alertId, dateKey }: TimeCellProps) => {
	const {
		alertsStore: { getTaskAlert }
	} = useStores();
	const alert = getTaskAlert(alertId);
	if (!alert) {
		return null;
	}
	const { [dateKey]: dateString } = alert.exception;

	return <CommonTimeCell dateString={dateString} timezone={alert.timezone} />;
});

interface WayPointTimeCellProps {
	alertId: string;
	wayPointId: 'first' | 'last' | number;
	dateKey: keyof WayPointDateKeys;
}

export const WayPointTimeCell = observer(({ alertId, wayPointId, dateKey }: WayPointTimeCellProps) => {
	const {
		alertsStore: { getTaskAlert }
	} = useStores();
	const alert = getTaskAlert(alertId);

	if (!alert) {
		return null;
	}

	const { way_points } = alert.exception;
	let wayPoint = null;

	if (Array.isArray(way_points) && way_points.length) {
		switch (wayPointId) {
			case 'first':
				wayPoint = way_points[0] || null;
				break;
			case 'last':
				wayPoint = last(way_points) || null;
				break;
			default:
				wayPoint = way_points.find(({ id }) => id === wayPointId);
		}
	}

	const dateString = wayPoint ? wayPoint[dateKey] : null;

	return <CommonTimeCell dateString={dateString} timezone={alert.timezone} />;
});
